import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowRight,
  LayoutDashboard,
  Tag,
  Clock,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/core/Button';
import { useTranslation } from '../context/LanguageContext';
import { PLAN_NAME_KEYS } from '../i18n';
import { getMySubscription } from '../api/subscription.api';
import type { UserSubscription } from '../api/subscription.api';

type ResultType = 'success' | 'fail' | 'cancel';

export function PaymentResultPage({ result }: { result: ResultType }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tranId = searchParams.get('tran_id');

  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [subLoading, setSubLoading] = useState(result === 'success');

  useEffect(() => {
    if (result !== 'success') return;
    // Poll up to 5 times with 1.5s delay — IPN may arrive slightly after redirect
    let attempts = 0;
    const poll = async () => {
      try {
        const data = await getMySubscription();
        if (data) {
          setSubscription(data);
          setSubLoading(false);
          return;
        }
      } catch {
        // ignore, keep polling
      }
      attempts++;
      if (attempts < 5) setTimeout(poll, 1500);
      else setSubLoading(false);
    };
    poll();
  }, [result]);

  if (result === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {/* Success card */}
          <div className="bg-slate-800 border border-emerald-500/30 rounded-2xl p-8 shadow-2xl shadow-emerald-500/10 text-center mb-4">
            {/* Icon */}
            <div className="relative inline-flex items-center justify-center mb-6">
              <div className="w-24 h-24 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
              </div>
              <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-yellow-400" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">{t('payment.successTitle')}</h1>
            <p className="text-slate-400 mb-6">{t('payment.successBody')}</p>

            {/* Subscription info */}
            {subscription && (
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-slate-700/50 rounded-xl p-3">
                  <Tag className="w-4 h-4 text-violet-400 mx-auto mb-1" />
                  <p className="text-xs text-slate-500 mb-0.5">{t('payment.plan')}</p>
                  <p className="text-sm font-semibold text-white">
                    {t(PLAN_NAME_KEYS[subscription.plan])}
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-3">
                  <LayoutDashboard className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <p className="text-xs text-slate-500 mb-0.5">{t('payment.pdfsPerMonth')}</p>
                  <p className="text-sm font-semibold text-white">
                    {subscription.limits.pdfsPerMonth}
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-3">
                  <Clock className="w-4 h-4 text-sky-400 mx-auto mb-1" />
                  <p className="text-xs text-slate-500 mb-0.5">{t('payment.expires')}</p>
                  <p className="text-sm font-semibold text-white">
                    {new Date(subscription.periodEnd).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}

            {subLoading && !subscription && (
              <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mb-6">
                <div className="w-4 h-4 border-2 border-slate-600 border-t-violet-400 rounded-full animate-spin" />
                {t('payment.activating')}
              </div>
            )}

            {/* Transaction ID */}
            {tranId && (
              <p className="text-slate-600 text-xs font-mono mb-6 bg-slate-900/50 rounded-lg px-3 py-2">
                {t('payment.tranId', { id: tranId })}
              </p>
            )}

            <Button
              size="lg"
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-emerald-600 to-violet-600 hover:from-emerald-500 hover:to-violet-500 text-white font-semibold rounded-xl"
            >
              {t('payment.goToDashboard')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-center text-slate-600 text-xs">{t('payment.receipt')}</p>
        </div>
      </div>
    );
  }

  if (result === 'fail') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-slate-800 border border-red-500/30 rounded-2xl p-8 shadow-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">{t('payment.failTitle')}</h1>
          <p className="text-slate-400 mb-2">{t('payment.failBody')}</p>
          {tranId && (
            <p className="text-slate-600 text-xs font-mono mb-6 bg-slate-900/50 rounded-lg px-3 py-2">
              {t('payment.tranId', { id: tranId })}
            </p>
          )}
          <div className="flex flex-col gap-3 mt-6">
            <Button
              size="lg"
              onClick={() => navigate('/pricing')}
              className="w-full bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-semibold"
            >
              {t('payment.tryAgain')}
            </Button>
            <button
              onClick={() => navigate('/')}
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              {t('payment.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // cancel
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-800 border border-yellow-500/30 rounded-2xl p-8 shadow-2xl text-center">
        <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-yellow-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">{t('payment.cancelTitle')}</h1>
        <p className="text-slate-400 mb-6">{t('payment.cancelBody')}</p>
        <div className="flex flex-col gap-3">
          <Button
            size="lg"
            onClick={() => navigate('/pricing')}
            className="w-full bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-semibold"
          >
            {t('payment.viewPlans')}
          </Button>
          <button
            onClick={() => navigate('/')}
            className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
          >
            {t('payment.backToDashboard')}
          </button>
        </div>
      </div>
    </div>
  );
}
